import { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Copy, Code, ExternalLink, Pencil, Trash2, Link2, Loader2, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { BookingLinkForm, emptyLink } from './BookingLinkForm';

const publicUrl = (slug) => `${window.location.origin}/rdv/${slug}`;
const iframeCode = (slug) =>
  `<iframe src="${publicUrl(slug)}?embed=1" width="100%" height="780" style="border:0;border-radius:12px;" title="Prendre rendez-vous"></iframe>`;

const copy = async (txt, msg) => {
  try { await navigator.clipboard.writeText(txt); toast.success(msg); }
  catch { toast.error('Copie impossible'); }
};

function LinkRow({ link, onEdit, onToggle, onDelete }) {
  return (
    <div className="border border-slate-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3 hover:border-slate-300 transition-colors"
         data-testid={`booking-link-row-${link.link_id}`}>
      <div className="w-1.5 self-stretch rounded-full hidden sm:block" style={{ background: link.color }} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-slate-900 truncate">{link.title}</span>
          {!link.active && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">Inactif</span>}
        </div>
        <div className="text-xs text-slate-500 flex items-center gap-3 mt-0.5 flex-wrap">
          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{link.duration_min} min</span>
          <span>{link.event_type_label}</span>
          <span data-testid={`booking-link-count-${link.link_id}`}>{link.bookings_count || 0} réservation(s)</span>
          <span className="text-blue-700 truncate">/rdv/{link.slug}</span>
        </div>
      </div>
      <div className="flex items-center gap-1 flex-wrap">
        <Switch checked={link.active} onCheckedChange={() => onToggle(link)}
                data-testid={`booking-link-active-${link.link_id}`} />
        <Button variant="ghost" size="sm" title="Copier le lien" data-testid={`booking-link-copy-${link.link_id}`}
                onClick={() => copy(publicUrl(link.slug), 'Lien copié')}><Copy className="w-4 h-4" /></Button>
        <Button variant="ghost" size="sm" title="Copier le code iframe" data-testid={`booking-link-iframe-${link.link_id}`}
                onClick={() => copy(iframeCode(link.slug), 'Code iframe copié')}><Code className="w-4 h-4" /></Button>
        <Button variant="ghost" size="sm" title="Ouvrir" data-testid={`booking-link-open-${link.link_id}`}
                onClick={() => window.open(publicUrl(link.slug), '_blank')}><ExternalLink className="w-4 h-4" /></Button>
        <Button variant="ghost" size="sm" title="Modifier" data-testid={`booking-link-edit-${link.link_id}`}
                onClick={() => onEdit(link)}><Pencil className="w-4 h-4" /></Button>
        <Button variant="ghost" size="sm" title="Supprimer" data-testid={`booking-link-delete-${link.link_id}`}
                onClick={() => onDelete(link)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
      </div>
    </div>
  );
}

export function BookingLinksDialog({ open, onOpenChange, meta, usersList }) {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/agenda/booking-links'); setLinks(r.data.links || []); }
    catch { toast.error('Erreur chargement des liens'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) { load(); setEditing(null); } }, [open, load]);

  const toggle = async (l) => {
    const { link_id, event_type_label, bookings_count, created_at, updated_at, created_by,
      created_by_email, deleted_at, last_booking_at, ...rest } = l;
    try { await api.put(`/agenda/booking-links/${link_id}`, { ...rest, active: !l.active }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail?.toString() || 'Erreur'); }
  };
  const remove = async (l) => {
    if (!window.confirm(`Supprimer le lien « ${l.title} » ? Il ne sera plus accessible.`)) return;
    try { await api.delete(`/agenda/booking-links/${l.link_id}`); toast.success('Lien supprimé'); load(); }
    catch { toast.error('Erreur suppression'); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="booking-links-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Link2 className="w-5 h-5 text-blue-700" />Liens de réservation</DialogTitle>
          <DialogDescription className="sr-only">Créer et gérer les liens publics de prise de rendez-vous</DialogDescription>
        </DialogHeader>
        {editing ? (
          <BookingLinkForm initial={editing} meta={meta} usersList={usersList}
                           onBack={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-slate-500">Partagez ces liens ou intégrez-les sur votre site : chaque réservation crée un événement dans l'Agenda.</p>
              <Button size="sm" onClick={() => setEditing(emptyLink())} data-testid="booking-links-new-btn">
                <Plus className="w-4 h-4 mr-1" /> Nouveau lien
              </Button>
            </div>
            {loading ? (
              <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-blue-600" /></div>
            ) : links.length === 0 ? (
              <div className="text-center py-10 text-sm text-slate-500 border border-dashed rounded-xl" data-testid="booking-links-empty">
                Aucun lien pour le moment. Créez votre premier lien de réservation.
              </div>
            ) : links.map((l) => (
              <LinkRow key={l.link_id} link={l} onEdit={setEditing} onToggle={toggle} onDelete={remove} />
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
